import React, { type MouseEvent } from 'react';
import { Link as GatsbyLink, navigate } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlgoliaHit } from 'instantsearch.js';

import {
  Empty, Container, Heading, Card, Grid, Transition, Link, Button,
} from '@printdeal/ui-library';

import ProductResults, { type ClickEventProp } from './ProductSearchResults';
import { HitProp } from '../../../algolia-search/types';
import { useReadyForCypress } from '../../../../hooks/useReadyForCypress';
import useSystemPages from '../../../../hooks/useSystemPages';
import { useUniqueUserIdentity } from '../../../../hooks/useUniqueUserIdentity';
import { getConfiguration } from '../../../../helpers/Configuration';

interface FormattedMessageProp {
  id: string;
  description?: string;
  defaultMessage: string;
}

export interface SearchResultsProps {
  productResults: HitProp[];
  categoryResults: HitProp[];
  contentPageResults: HitProp[];
  productQueryId: string | null;
  categoryQueryId: string | null;
  contentPageQueryId: string | null;
  productIndex: string | null;
  categoryIndex: string | null;
  contentPageIndex: string | null;
  totalProductsCount?: number;
}

interface LiveSearchMessages {
  formattedTitle: FormattedMessageProp;
  formattedProductTitle: FormattedMessageProp;
  formattedCategoryTitle: FormattedMessageProp;
  formattedContentPageTitle: FormattedMessageProp;
  formattedNoResultsText: FormattedMessageProp;
  formattedNoResultsTitle: FormattedMessageProp;
  formattedAllSearch: FormattedMessageProp;
  formattedNoResultsCTAButtonText: FormattedMessageProp;
}

interface LiveSearchProps {
  searchResults: SearchResultsProps;
  clickEvent?: (prop: AlgoliaHit) => Promise<void>;
  url: string;
  messages: LiveSearchMessages,
  totalSearchResults?: number;
  isOpen?: boolean;
  onClose?: (event?: MouseEvent<HTMLButtonElement>) => void;
  handleOnSearchResultsClickGA4?: Function;
  handleShowAllSearchResultsClickGA?: Function;
  handleNoResultsFoundCTARequestQuote?: Function;
  staticHeaderInView?: boolean;
}

const SearchResultsDropdown = ({
  searchResults,
  clickEvent = async () => { },
  handleOnSearchResultsClickGA4 = () => { },
  handleShowAllSearchResultsClickGA = () => { },
  handleNoResultsFoundCTARequestQuote = () => { },
  totalSearchResults = 0,
  messages,
  url,
  isOpen,
  onClose,
  staticHeaderInView,
}: LiveSearchProps) => {
  const intl = useIntl();
  const { userId } = useUniqueUserIdentity();
  const [accountDashboard] = useSystemPages('accountDashboard');
  const quoteRequestPath = `${accountDashboard?.path}/quoting/request/new`;
  const configuration = getConfiguration();
  const emptyResultsMessageFrance = '';

  const {
    formattedNoResultsTitle,
    formattedProductTitle,
    formattedCategoryTitle,
    formattedContentPageTitle,
    formattedNoResultsText,
    formattedAllSearch,
    formattedNoResultsCTAButtonText,
  } = messages;

  const {
    productResults,
    categoryResults,
    contentPageResults,
    productQueryId,
    categoryQueryId,
    contentPageQueryId,
    productIndex,
    categoryIndex,
    contentPageIndex,
  } = searchResults;

  const readyForCypressId = useReadyForCypress('live-search');

  const handleLiveResultsClickGA4 = (productName: string | undefined, position: number, type: string) => {
    handleOnSearchResultsClickGA4(productName, position, 'overlay', type);
  };

  const productAnalyticsEvent = (item: ClickEventProp) => {
    const {
      path, objectID, position, productName,
    } = item;
    clickEvent({
      productName,
      path,
      objectID,
      position,
      queryID: productQueryId,
      index: productIndex,
      staticHeaderInView,
      userId,
    } as AlgoliaHit);
  };

  const categoryAnalyticsEvent = (item: ClickEventProp) => {
    const { path, objectID, position } = item;
    clickEvent({
      path,
      objectID,
      position,
      queryID: categoryQueryId,
      index: categoryIndex,
      staticHeaderInView,
      userId,
    } as AlgoliaHit);
  };

  const contentPageAnalyticsEvent = (item: ClickEventProp) => {
    const { path, objectID, position } = item;
    clickEvent({
      path,
      objectID,
      position,
      queryID: contentPageQueryId,
      index: contentPageIndex,
      staticHeaderInView,
      userId,
    } as AlgoliaHit);
  };

  const handleRequestQuoteClickWhenNoOrders = () => {
    handleNoResultsFoundCTARequestQuote();
    navigate(quoteRequestPath);
  };

  const handleEmptyResultsDisplay = configuration?.quote?.enabled === false ? (
    <Empty
      title={intl.formatMessage({
        id: formattedNoResultsTitle.id,
        defaultMessage: formattedNoResultsTitle.defaultMessage,
      })}
      description={emptyResultsMessageFrance}
    />
  ) : (
    <Empty
      title={intl.formatMessage({
        id: formattedNoResultsTitle.id,
        defaultMessage: formattedNoResultsTitle.defaultMessage,
      })}
      description={(
        <FormattedMessage
          id={formattedNoResultsText.id}
          defaultMessage={formattedNoResultsText.defaultMessage}
        />
        )}
      action={(
        <Button type="button" onClick={handleRequestQuoteClickWhenNoOrders}>
          <FormattedMessage
            id={formattedNoResultsCTAButtonText.id}
            defaultMessage={formattedNoResultsCTAButtonText.defaultMessage}
          />
        </Button>
        )}
    />
  );

  return (
    <Transition show={isOpen} className="pd-absolute pd-inset-x-0 pd-top-full -pd-mt-px pd-h-screen">
      <Transition.Child onClick={onClose}>
        <div className="pd-absolute pd-inset-0 pd-bg-black pd-opacity-20 pd-outline-none" />
      </Transition.Child>
      <Transition.Child
        variant="appear-from-top"
        className="pd-transform-gpu pd-border-t pd-bg-white"
        data-cy={readyForCypressId}
      >
        <div className="pd-max-h-[calc(100vh-5.5rem)] pd-overflow-auto">
          <Container className="pd-flex pd-max-w-[66.25rem] pd-flex-col pd-gap-7 pd-pb-8 pd-pt-4">
            {totalSearchResults > 0 ? (
              <div className="pd-flex pd-justify-end">
                <Link
                  as={GatsbyLink}
                  to={url}
                  onClick={() => {
                    handleShowAllSearchResultsClickGA(staticHeaderInView);
                    onClose?.();
                  }}
                  variant="secondary"
                  iconBefore="chevron-right"
                  className="pd-text-sm"
                  data-cy="total-search-results-link"
                >
                  <FormattedMessage
                    id={formattedAllSearch.id}
                    description={formattedAllSearch.description}
                    defaultMessage={formattedAllSearch.defaultMessage}
                    values={{
                      count: totalSearchResults,
                    }}
                  />
                </Link>
              </div>
            ) : (
              <>{ handleEmptyResultsDisplay }</>
            )}
            {productResults?.length > 0 && (
              <section data-cy="live-search-section" className="pd-flex pd-flex-col pd-gap-4">
                <Heading as="h4" size="2" variant="display">
                  <FormattedMessage
                    id={formattedProductTitle.id}
                    defaultMessage={formattedProductTitle?.defaultMessage}
                  />
                </Heading>
                <ProductResults
                  products={productResults}
                  dispatchProductsClickGA4={handleLiveResultsClickGA4}
                  clickEvent={productAnalyticsEvent}
                />
              </section>
            )}
            {categoryResults?.length > 0 && (
              <section data-cy="live-search-section" className="pd-flex pd-flex-col pd-gap-4">
                <Heading as="h4" size="2" variant="display">
                  <FormattedMessage
                    id={formattedCategoryTitle.id}
                    defaultMessage={formattedCategoryTitle?.defaultMessage}
                  />
                </Heading>
                <Grid className="pd-items-stretch pd-gap-4 md:pd-grid-cols-2">
                  {categoryResults.map((hit: HitProp, index: number) => (
                    <Card
                      key={hit.objectID}
                      variant="horizontal"
                      title={<span className="pd-font-bold pd-text-dim-800">{hit.categoryName}</span>}
                      link={(
                        <GatsbyLink
                          to={hit.path}
                          onClick={() => {
                            categoryAnalyticsEvent({ ...hit, position: index });
                            handleLiveResultsClickGA4(hit?.categoryName, index + 1, 'categorypage');
                          }}
                        />
                      )}
                    />
                  ))}
                </Grid>
              </section>
            )}
            {contentPageResults?.length > 0 && (
              <section data-cy="live-search-section" className="pd-flex pd-flex-col pd-gap-4">
                <Heading as="h4" size="2" variant="display">
                  <FormattedMessage
                    id={formattedContentPageTitle.id}
                    defaultMessage={formattedContentPageTitle?.defaultMessage}
                  />
                </Heading>
                <Grid className="pd-items-stretch pd-gap-4 md:pd-grid-cols-2">
                  {contentPageResults.map((hit: HitProp, index: number) => (
                    <Card
                      key={hit.objectID}
                      variant="horizontal"
                      title={<span className="pd-font-bold pd-text-dim-800">{hit.title}</span>}
                      link={(
                        <GatsbyLink
                          to={hit.path}
                          onClick={() => {
                            contentPageAnalyticsEvent({ ...hit, position: index });
                            handleLiveResultsClickGA4(hit?.title, index + 1, 'contentpage');
                          }}
                        />
                      )}
                    />
                  ))}
                </Grid>
              </section>
            )}
          </Container>
        </div>
      </Transition.Child>
    </Transition>
  );
};

export default SearchResultsDropdown;
